/**
 * The FM Technician's experience (FM Technician rebuild, R1 pieces): who gets
 * it, the "No site yet" rule, and what only a technician's menu offers.
 *
 * WHO. By PERMISSION, never a role name: somebody who WORKS jobs
 * (maintenance:work) without reading the whole register (maintenance:view_all).
 * That is the server's own test for a technician-shaped user
 * (utils/site_scope._empty_means_none) and the one the offline queue uses to
 * ask for persistent storage (services/pendingQueue keepStorageForFieldWork).
 * A contractor holds maintenance:work too, but signs in to its own portal.
 *
 * NO SITE (C2, D5b). /auth/me says `site_scope.mode === "none"` for a
 * technician the FM has not placed at a site yet. Every job route answers
 * nothing for them, so the app does not pretend: RouteGate sends them to
 * /tech/no-site, and only their own self-service stays open (a desk, their
 * visitors, their requests, their profile, safety). Read the server's mode —
 * never `site_ids.length === 0`, which means ALL sites for an FM Manager.
 */
import { matchPath } from 'react-router-dom';
import { hasPermFor } from '@/utils/permissions';
import type { SiteScope, UserRole } from '@/types';

type TechUser = { role: UserRole; permissions?: string[] | null; site_scope?: SiteScope | null } | null | undefined;

export function isTechExperience(user: TechUser): boolean {
  return !!user && hasPermFor(user, 'maintenance:work') && !hasPermFor(user, 'maintenance:view_all');
}

export const TECH_NO_SITE = '/tech/no-site';
/** Reception R2: the same rule, in the desk's own words. */
export const RECEPTION_NO_BUILDING = '/reception/no-building';

/** Where a person with no site goes (the server's site_scope says "none"). */
export function noSitePath(user: TechUser): string {
  return hasPermFor(user, 'reception:desk') ? RECEPTION_NO_BUILDING : TECH_NO_SITE;
}

/** A technician with no site yet (the server's site_scope, D5b). */
export function hasNoSite(user: TechUser): boolean {
  return user?.site_scope?.mode === 'none';
}

/** What stays open to a person with no site: the no-site screen and their
 *  own employee self-service. Route patterns, as ROUTE_ACCESS keys. */
export const NO_SITE_OPEN: readonly string[] = [
  TECH_NO_SITE, RECEPTION_NO_BUILDING,
  '/book', '/my-bookings', '/office-days', '/whos-in',
  '/my-visitors',
  '/my-requests', '/my-requests/:id',
  '/notifications', '/settings', '/settings/siri-shortcuts', '/profile',
  '/my-safety', '/safety', '/safety/roll-call/:alertId', '/safety-incidents/:id',
  // R2 "Me": the first run ends on the no-site screen; their own alerts,
  // profile and what is saved on the phone are self-service.
  '/tech/welcome', '/tech/alerts', '/tech/profile', '/tech/sync',
  // R15: anyone may raise a private concern, with a building or without one.
  '/reception/concern', '/reception/concerns',
];

export function openWithNoSite(pathname: string): boolean {
  return NO_SITE_OPEN.some((pattern) => matchPath({ path: pattern, end: true }, pathname));
}

/** The red "Report incident" entry (C14, D11). */
export const TECH_INCIDENT = '/tech/incident';

/** Routes the general sidebar offers to the technician experience and nobody
 *  else, so no other role's menu grows an item (as EMPLOYEE_ONLY_NAV). */
export const TECH_ONLY_NAV: readonly string[] = [TECH_INCIDENT];

// ---------------------------------------------------------------------------
// Release 2 — the technician's own shell (C1, D1; DESIGN-CHECK "Pattern to
// follow": a config picks the experience, bottomNav swaps the bar, Sidebar
// takes the groups).
// ---------------------------------------------------------------------------

/**
 * Who gets the technician's SHELL — its menu, phone bar, top-bar pieces and
 * running-job bar: the technician experience above, working the site's
 * tickets as well as its work orders (helpdesk:work, the technician's ticket
 * permission — R1 A.0). The second half keeps the contractor role (it holds
 * maintenance:work without maintenance:view_all too, but works only the jobs
 * sent to its firm and has no ticket queue) on the menu it had.
 */
export function isTechShell(user: TechUser): boolean {
  return isTechExperience(user) && hasPermFor(user, 'helpdesk:work');
}

/** The technician's home (C3). Until the page ships, the dashboard (with the
 *  R1 check-in card) stays their home — see techHomePath. */
export const TECH_HOME = '/tech';

export interface TechNavEntry {
  /** Stable key (icons, test ids): the target path moves, this does not. */
  id: string;
  label: string;
  /** The technician page that does the job. */
  path: string;
  /** The existing page that does it until `path` ships. */
  fallback?: string;
  /** Shown only while THIS page is not offered: the interim item it stands
   *  in for disappears the day its replacement ships. */
  until?: string;
  /** A permission beyond the route's own (Shift lead: fm:shift_lead, D6). */
  requires?: string;
}
export interface TechNavGroup { id: string; heading: string; items: TechNavEntry[] }

/**
 * The prototype's groups (INVENTORY "Shell"): Today · My work · Safety ·
 * Shift · Me.
 *
 * Release 3 (plant room and safety) adds its items here as its pages ship:
 * Log rounds (My work, slice S8), Permits and Locks (Safety) and Tools &
 * instruments (Me) (slice S9), Emergency steps (Safety, slice S10). An item
 * is never listed before its page exists — not even hidden.
 *
 * 🔴 A PAGE NOT YET BUILT IS NEVER A LINK. Every item resolves through the
 * route map: its technician page when that page is declared (ROUTE_ACCESS),
 * else the existing page that already does the job (`fallback`), else it is
 * not shown. So a slice that ships /tech/jobs adds its ROUTE_ACCESS entry and
 * App route, and "My jobs" moves to it with no edit here.
 */
export const TECH_NAV_GROUPS: readonly TechNavGroup[] = [
  { id: 'today', heading: 'Today', items: [
    // The literal (=== TECH_HOME), so the route sweep sees the menu's link to it.
    { id: 'home', label: 'Home', path: '/tech', fallback: '/dashboard' },
    { id: 'alerts', label: 'Alerts', path: '/tech/alerts', fallback: '/notifications' },
  ] },
  { id: 'work', heading: 'My work', items: [
    { id: 'jobs', label: 'My jobs', path: '/tech/jobs', fallback: '/maintenance' },
    // Until the one job list (D19) ships, tickets live on their own page.
    { id: 'tickets', label: 'Tickets', path: '/helpdesk', until: '/tech/jobs' },
    { id: 'scan', label: 'Scan asset', path: '/tech/scan', fallback: '/scan' },
    // R3 (C9): the plant-room rounds and meter readings.
    { id: 'rounds', label: 'Log rounds', path: '/tech/rounds' },
    { id: 'fault', label: 'Report fault', path: '/tech/report-fault' },
  ] },
  { id: 'safety', heading: 'Safety', items: [
    { id: 'permits', label: 'Permits', path: '/tech/permits' },
    { id: 'locks', label: 'Locks', path: '/tech/locks' },
    { id: 'emergency', label: 'Emergency steps', path: '/tech/emergency' },
    { id: 'incident', label: 'Report incident', path: TECH_INCIDENT },
  ] },
  { id: 'shift', heading: 'Shift', items: [
    { id: 'handover', label: 'Shift handover', path: '/tech/handover' },
    { id: 'lead', label: 'Shift lead', path: '/tech/lead', requires: 'fm:shift_lead' },
    { id: 'my-work', label: 'My work', path: '/tech/my-work' },
  ] },
  { id: 'me', heading: 'Me', items: [
    // Until the profile page ships, the sidebar's pinned Settings is theirs.
    { id: 'profile', label: 'Profile & settings', path: '/tech/profile' },
    { id: 'tools', label: 'Tools & instruments', path: '/tech/tools' },
    // C23: their own employee self-service, on the existing pages.
    { id: 'book', label: 'Book a desk', path: '/book' },
    { id: 'visitors', label: 'My visitors', path: '/my-visitors' },
    { id: 'requests', label: 'My requests', path: '/my-requests' },
  ] },
];

/** An item as shown: where it goes now. */
export interface TechNavItem { id: string; label: string; path: string }
export interface ResolvedTechNavGroup { id: string; heading: string; items: TechNavItem[] }

type CanOffer = (path: string) => boolean;

/** Where an entry goes for this user now, or null when it is not shown. */
export function resolveTechEntry(user: TechUser, entry: TechNavEntry, canOffer: CanOffer): string | null {
  if (entry.requires && !hasPermFor(user, entry.requires)) return null;
  if (entry.until && canOffer(entry.until)) return null;
  if (canOffer(entry.path)) return entry.path;
  if (entry.fallback && canOffer(entry.fallback)) return entry.fallback;
  return null;
}

/** The technician's sidebar groups, each item resolved; empty groups dropped.
 *  `canOffer` is routeAccess.canOfferInNav for this user (passed in: the
 *  route map imports this file). */
export function techNavGroups(user: TechUser, canOffer: CanOffer): ResolvedTechNavGroup[] {
  const seen = new Set<string>();
  return TECH_NAV_GROUPS
    .map((g) => ({ id: g.id, heading: g.heading, items: g.items.flatMap((e) => {
      const path = resolveTechEntry(user, e, canOffer);
      if (!path || seen.has(path)) return [];
      seen.add(path);
      return [{ id: e.id, label: e.label, path }];
    }) }))
    .filter((g) => g.items.length > 0);
}

/**
 * The phone bar (D1): Home · Jobs · Scan · Rounds · Menu. Rounds is R3 (C9);
 * in R2 its slot was Alerts (interpretation 84) — R3 puts Rounds back in it,
 * and Alerts stays in the menu (Today group). Until /tech/rounds is declared
 * for this person, the slot falls back to Alerts so the bar keeps five items.
 * `path: null` is Menu, which opens the drawer.
 */
export const TECH_BOTTOM_NAV: readonly (TechNavEntry | { id: 'menu'; label: 'Menu'; path: null })[] = [
  { id: 'home', label: 'Home', path: TECH_HOME, fallback: '/dashboard' },
  { id: 'jobs', label: 'Jobs', path: '/tech/jobs', fallback: '/maintenance' },
  { id: 'scan', label: 'Scan', path: '/tech/scan', fallback: '/scan' },
  { id: 'rounds', label: 'Rounds', path: '/tech/rounds' },
  // Shown only while Rounds is not offered (the bar keeps five items).
  { id: 'alerts', label: 'Alerts', path: '/tech/alerts', fallback: '/notifications', until: '/tech/rounds' },
  { id: 'menu', label: 'Menu', path: null },
];

/** The bar as shown: (id, label, path | null for Menu). */
export function techBottomNav(user: TechUser, canOffer: CanOffer): { id: string; label: string; path: string | null }[] {
  return TECH_BOTTOM_NAV.flatMap((e): { id: string; label: string; path: string | null }[] => {
    if (e.path === null) return [{ id: e.id, label: e.label, path: null }];
    const path = resolveTechEntry(user, e, canOffer);
    return path ? [{ id: e.id, label: e.label, path }] : [];
  });
}

/** The technician's home: /tech once that page is declared, else the dashboard. */
export function techHomePath(canOpen: CanOffer): string {
  return canOpen(TECH_HOME) ? TECH_HOME : '/dashboard';
}

/** The menu item a pathname belongs to: the longest matching resolved path. */
export function techActivePath(pathname: string, paths: readonly string[]): string | null {
  let best: string | null = null;
  for (const p of paths) {
    if (pathname === p || pathname.startsWith(p + '/')) {
      if (!best || p.length > best.length) best = p;
    }
  }
  return best;
}

/** Where a job opens (B27 "tap to open"): the technician's job page once it
 *  ships (C6), else the existing work-order or ticket page; null when there
 *  is no page this person can open (the bar then shows, but does not link). */
export function techJobPath(kind: 'wo' | 'ticket' | 'inspection', id: string, canOpen: CanOffer): string | null {
  if (canOpen('/tech/jobs/:kind/:id')) return `/tech/jobs/${kind}/${id}`;
  if (kind === 'wo' && canOpen('/maintenance/work-orders/:id')) return `/maintenance/work-orders/${id}`;
  if (kind === 'ticket' && canOpen('/helpdesk/:id')) return `/helpdesk/${id}`;
  return null;
}
